// Orchestrates the hero live-transcription demo.
// Two modes share one lyrics view:
//   - "sim"  : auto-playing simulated meeting (always works, ambient)
//   - "live" : real microphone via the backend proxy (provider hidden)
import React, { useCallback, useEffect, useRef, useState } from "react";
import { LyricsTranscript, type DisplayLine } from "./LyricsTranscript";
import { DEMO_LINES } from "../lib/demoScript";
import { LiveSession, type LiveStatus, type LiveToken } from "../lib/liveClient";
import { IconMic } from "./Icons";

type Mode = "sim" | "live";

interface Committed {
  id: string;
  who: string;
  text: string;
}

const MAX_KEEP = 7;

export function LiveDemo() {
  const [mode, setMode] = useState<Mode>("sim");
  const [committed, setCommitted] = useState<Committed[]>([]);
  const [partial, setPartial] = useState<{ who: string; text: string } | null>(null);
  const [status, setStatus] = useState<LiveStatus>("idle");
  const [note, setNote] = useState<string>("Minh hoạ trực tiếp");

  const sessionRef = useRef<LiveSession | null>(null);
  const simTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idRef = useRef(0);
  const liveSpeakerRef = useRef<string>("1");
  const liveLabelRef = useRef<Record<string, string>>({});

  const nextId = () => `l${idRef.current++}`;

  // ─────────────────────────── simulated mode ───────────────────────────
  const stopSim = () => {
    if (simTimer.current) clearTimeout(simTimer.current);
    simTimer.current = null;
  };

  const runSim = useCallback(() => {
    stopSim();
    let li = 0;
    let wordIdx = 0;
    const committedLocal: Committed[] = [];

    const tick = () => {
      const line = DEMO_LINES[li];
      const words = line.text.split(" ");
      wordIdx++;
      const shown = words.slice(0, wordIdx).join(" ");
      setPartial({ who: line.speakerLabel, text: shown });

      if (wordIdx >= words.length) {
        // Commit the finished line, pause, then move on.
        simTimer.current = setTimeout(() => {
          committedLocal.push({ id: nextId(), who: line.speakerLabel, text: line.text });
          while (committedLocal.length > MAX_KEEP) committedLocal.shift();
          setCommitted([...committedLocal]);
          setPartial(null);
          li = (li + 1) % DEMO_LINES.length;
          wordIdx = 0;
          // When looping back to the top, fade the history out for a fresh start.
          if (li === 0) {
            committedLocal.length = 0;
            setCommitted([]);
          }
          simTimer.current = setTimeout(tick, 520);
        }, 650);
        return;
      }
      // Reveal next word — vary the cadence slightly for a natural feel.
      const base = 150 + (wordIdx % 3) * 55;
      simTimer.current = setTimeout(tick, base);
    };

    setCommitted([]);
    setPartial(null);
    simTimer.current = setTimeout(tick, 400);
  }, []);

  // Auto-start ambient simulation on mount.
  useEffect(() => {
    if (mode === "sim") runSim();
    return stopSim;
  }, [mode, runSim]);

  // ─────────────────────────── live mode ───────────────────────────
  const handleTokens = useCallback((tokens: LiveToken[]) => {
    const finals = tokens.filter((t) => t.is_final);
    const nonFinals = tokens.filter((t) => !t.is_final);

    if (finals.length > 0) {
      setCommitted((prev) => {
        const out = [...prev];
        for (const t of finals) {
          const sp = t.speaker ?? liveSpeakerRef.current;
          liveSpeakerRef.current = sp;
          if (!liveLabelRef.current[sp]) {
            const n = Object.keys(liveLabelRef.current).length + 1;
            liveLabelRef.current[sp] = `Người nói ${n}`;
          }
          const who = liveLabelRef.current[sp];
          const last = out[out.length - 1];
          if (last && last.who === who) {
            last.text = (last.text + t.text).replace(/\s+/g, " ");
          } else {
            out.push({ id: nextId(), who, text: t.text.trim() });
          }
        }
        while (out.length > MAX_KEEP) out.shift();
        return out;
      });
    }

    if (nonFinals.length > 0) {
      const sp = nonFinals[nonFinals.length - 1].speaker ?? liveSpeakerRef.current;
      const who = liveLabelRef.current[sp] ?? "Đang nghe";
      const text = nonFinals.map((t) => t.text).join("").trim();
      setPartial(text ? { who, text } : null);
    } else if (finals.length > 0) {
      setPartial(null);
    }
  }, []);

  const startLive = useCallback(async () => {
    stopSim();
    setMode("live");
    setCommitted([]);
    setPartial(null);
    liveLabelRef.current = {};
    liveSpeakerRef.current = "1";
    setNote("Đang kết nối…");

    const session = new LiveSession({
      onStatus: (s) => {
        setStatus(s);
        if (s === "listening") setNote("Đang nghe — hãy nói");
        if (s === "stopped") setNote("Đã dừng");
      },
      onTokens: handleTokens,
      onError: (msg, code) => {
        // Any live failure gracefully falls back to the simulation.
        setNote(code === "no_mic" ? "Không có micro — xem minh hoạ" : "Chưa khả dụng — xem minh hoạ");
        setStatus("idle");
        sessionRef.current = null;
        setMode("sim");
      },
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch {
      setMode("sim");
    }
  }, [handleTokens]);

  const stopLive = useCallback(() => {
    sessionRef.current?.stop();
    sessionRef.current = null;
    setStatus("idle");
    setMode("sim");
  }, []);

  useEffect(() => () => sessionRef.current?.stop(), []);

  // ─────────────────────────── view model ───────────────────────────
  const lines: DisplayLine[] = committed.map((c, i) => ({
    id: c.id,
    who: c.who,
    text: c.text,
    kind: !partial && i === committed.length - 1 ? "active" : "past",
  }));
  if (partial) {
    lines.push({ id: "partial", who: partial.who, text: partial.text, kind: "partial" });
  }

  const live = mode === "live" && (status === "listening" || status === "connecting");

  return (
    <div className="demo-card">
      <div className="demo-top">
        <span className="tl-dot tl-r" />
        <span className="tl-dot tl-y" />
        <span className="tl-dot tl-g" />
        <span className="title">Phiên âm trực tiếp</span>
        <span className="spacer" />
        <span className="demo-status">
          <span className={`dot ${live ? "live" : ""}`} />
          {live ? "TRỰC TIẾP" : "Demo"}
        </span>
      </div>

      <LyricsTranscript lines={lines} />

      <div className="demo-controls">
        {mode === "live" && (status === "listening" || status === "connecting") ? (
          <button className="mic-btn stop" onClick={stopLive}>
            <span className="equalizer">
              <i />
              <i />
              <i />
              <i />
              <i />
            </span>
            Dừng ghi
          </button>
        ) : (
          <button className="mic-btn" onClick={startLive}>
            <IconMic size={18} />
            Dùng micro của bạn
          </button>
        )}
        <span className="demo-hint">{note}</span>
      </div>
    </div>
  );
}
